import React, { useEffect, useMemo, useState } from "react";
import { Stack } from "@mui/material";
import FormDialog from "../../../../components/common/FormDialog";
import FormField from "../../../../components/common/FormField";
import PasswordField from "../../../../components/common/PasswordField";
import ChipAutocomplete from "../../../../components/common/ChipAutocomplete";
import useFormValidation from "../../../../hooks/useFormValidation";
import { matchField, minLength, password, required } from "../../../../utils/formValidation";

const createSchema = {
  name: [required("Name is required.")],
  username: [required("Username is required.")],
  password: [required("Password is required."), minLength(8), password({ min: 8 })],
  confirmPassword: [
    required("Confirm password is required."),
    matchField("password", "Passwords do not match."),
  ],
};

const TenantUserFormDialog = ({
  open,
  roles = [],
  rolesLoading = false,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const [formError, setFormError] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);

  const { values, getFieldProps, validateAll, reset } = useFormValidation(
    { name: "", username: "", password: "", confirmPassword: "" },
    createSchema
  );

  useEffect(() => {
    if (!open) return;
    setFormError("");
    reset({
      name: "",
      username: "",
      password: "",
      confirmPassword: "",
    });
    setSelectedRoles([]);
  }, [open, reset]);

  const roleOptions = useMemo(() => {
    const fromApi = (roles || []).map((role) =>
      typeof role === "string" ? role : role?.name || role?.role || String(role)
    );
    return Array.from(new Set([...fromApi, ...selectedRoles].filter(Boolean)));
  }, [roles, selectedRoles]);

  const handleSubmit = async () => {
    const { isValid, errors } = validateAll();
    if (!isValid) {
      setFormError(
        errors.name ||
          errors.username ||
          errors.password ||
          errors.confirmPassword ||
          "Please correct the highlighted fields."
      );
      return;
    }
    if (!selectedRoles.length) {
      setFormError("Select at least one role.");
      return;
    }

    setFormError("");
    try {
      await onSubmit({
        name: values.name.trim(),
        username: values.username.trim(),
        password: values.password,
        roles: selectedRoles,
      });
    } catch (err) {
      setFormError(err?.message || "Unable to save user.");
    }
  };

  return (
    <FormDialog
      open={open}
      title="Add User"
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={loading}
      error={formError}
      onClearError={() => setFormError("")}
      submitLabel="Create User"
      loadingLabel="Saving..."
    >
      <FormField label="Name" required {...getFieldProps("name")} />
      <FormField label="Username" required {...getFieldProps("username")} />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5}>
        <PasswordField label="Password" required {...getFieldProps("password")} />
        <PasswordField
          label="Confirm Password"
          required
          {...getFieldProps("confirmPassword")}
        />
      </Stack>
      <ChipAutocomplete
        label="Roles"
        required
        freeSolo
        options={roleOptions}
        value={selectedRoles}
        onChange={setSelectedRoles}
        loading={rolesLoading}
        disabled={loading}
        helperText="At least one role is required."
      />
    </FormDialog>
  );
};

export default TenantUserFormDialog;
